'use client';

import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n/context';

const TermsPage = () => {
  const { t, locale } = useTranslation();

  return (
    <section className="py-32">
      <div className="container max-w-4xl">
        <div className="mb-16 flex flex-col gap-6">
          <Badge variant="outline" className="w-fit text-black">
            {t.footer['Legal']}
          </Badge>
          <h1 className="font-serif text-4xl md:text-5xl text-black">
            {t.legal['Terms of Service']}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t.legal['Effective Date:']}{' '}
            {locale === 'ar' ? '٢٨ أكتوبر ٢٠٢٣' : '28th of October, 2023'}
          </p>
        </div>

        <div className="prose prose-sm prose-slate dark:prose-invert max-w-none text-black">
          <h2>{t.legal['1. Acceptance of Terms']}</h2>
          <p>
            {
              t.legal[
                'By accessing our website and using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to any part of these terms, you must not use our services.'
              ]
            }
          </p>

          <h2>{t.legal['2. Definitions']}</h2>
          <p>
            <strong>{t.legal['Service:']}</strong>{' '}
            {
              t.legal[
                'refers to all services provided by Open, including the website, software, features, tools, and applications.'
              ]
            }
          </p>
          <p>
            <strong>{t.legal['User:']}</strong>{' '}
            {
              t.legal[
                'refers to any individual or entity that accesses or uses our services.'
              ]
            }
          </p>
          <p>
            <strong>{t.legal['Account:']}</strong>{' '}
            {
              t.legal[
                "refers to the user's personal account created to access our services."
              ]
            }
          </p>

          <h2>{t.legal['3. Eligibility']}</h2>
          <p>
            {
              t.legal[
                'To use Open, you must be at least 18 years old or have parental consent if you are a minor. By using our service, you represent and warrant that you meet these eligibility requirements.'
              ]
            }
          </p>

          <h2>{t.legal['4. User Accounts']}</h2>
          <h3>{t.legal['4.1 Account Creation']}</h3>
          <p>
            {
              t.legal[
                'To access certain features of our services, you must create an account. When creating an account, you agree to provide accurate, current, and complete information and to update your information to keep it accurate, current, and complete.'
              ]
            }
          </p>

          <h3>{t.legal['4.2 Account Security']}</h3>
          <p>
            {
              t.legal[
                'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other breach of security.'
              ]
            }
          </p>

          <h3>{t.legal['4.3 Account Termination']}</h3>
          <p>
            {
              t.legal[
                'We reserve the right to suspend or terminate your account at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users or our business interests.'
              ]
            }
          </p>

          <h2>{t.legal['5. User Conduct']}</h2>
          <p>
            {
              t.legal[
                'You agree not to use the service for any unlawful purpose or to solicit others to perform or participate in any unlawful acts. You agree to abide by all applicable laws and regulations and to comply with the following restrictions:'
              ]
            }
          </p>
          <ul>
            <li>
              {
                t.legal[
                  'Do not use the service in any manner that could damage, disable, overburden, or impair the service.'
                ]
              }
            </li>
            <li>
              {
                t.legal[
                  'Do not engage in any form of data mining, robots, or similar data gathering or extraction methods.'
                ]
              }
            </li>
            <li>
              {
                t.legal[
                  'Do not interfere with or circumvent any security features of the service.'
                ]
              }
            </li>
          </ul>

          <h2>{t.legal['6. Intellectual Property Rights']}</h2>
          <h3>{t.legal['6.1 Ownership']}</h3>
          <p>
            {
              t.legal[
                'All content, features, and functionality on the Open platform, including but not limited to text, graphics, logos, software, and code, are the exclusive property of Open and are protected by intellectual property laws.'
              ]
            }
          </p>

          <h3>{t.legal['6.2 License to Use']}</h3>
          <p>
            {
              t.legal[
                'OpenChat, Inc grants you a limited, non-exclusive, non-transferable license to access and use the service for personal or business purposes. You may not reproduce, distribute, modify, or create derivative works based on the service without our express written consent.'
              ]
            }
          </p>

          <h2>{t.legal['7. Payment Terms']}</h2>
          <h3>{t.legal['7.1 Pricing']}</h3>
          <p>
            {
              t.legal[
                'Access to certain features of Open may require payment. The pricing for our plans is outlined on our Pricing page. We reserve the right to change our pricing at any time, with notice to users.'
              ]
            }
          </p>

          <h3>{t.legal['7.2 Payment Methods']}</h3>
          <p>
            {
              t.legal[
                'Payments can be made using the payment methods specified on our website. By providing your payment information, you represent and warrant that you are authorized to use that payment method.'
              ]
            }
          </p>

          <h3>{t.legal['7.3 Refund Policy']}</h3>
          <p>
            {
              t.legal[
                'All fees paid are non-refundable unless otherwise stated. Please refer to our Refund Policy for more details.'
              ]
            }
          </p>

          <h2>{t.legal['8. Third-Party Links and Services']}</h2>
          <p>
            {
              t.legal[
                'Our service may contain links to third-party websites or services that are not owned or controlled by Open. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that Open shall not be liable for any damages or losses caused or alleged to be caused by the use of any such content, goods, or services available on or through any such websites or services.'
              ]
            }
          </p>

          <h2>{t.legal['9. Disclaimers']}</h2>
          <p>
            {
              t.legal[
                'The service is provided on an "as is" and "as available" basis. Open makes no representations or warranties of any kind, express or implied, regarding the operation of the service or the information, content, materials, or products included on the service. You agree that your use of the service is at your own risk.'
              ]
            }
          </p>

          <h2>{t.legal['10. Limitation of Liability']}</h2>
          <p>
            {
              t.legal[
                'In no event shall Open, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of (or inability to access or use) the service; (ii) any conduct or content of any third party on the service; (iii) any content obtained from the service; and (iv) unauthorized access, use, or alteration of your transmissions or content.'
              ]
            }
          </p>

          <h2>{t.legal['11. Indemnification']}</h2>
          <p>
            {
              t.legal[
                "You agree to indemnify, defend, and hold harmless Open, its affiliates, officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorney's fees) arising out of or in any way connected with your use of the service, your violation of these Terms, or your infringement of any intellectual property or other rights of any person or entity."
              ]
            }
          </p>

          <h2>{t.legal['12. Changes to These Terms']}</h2>
          <p>
            {
              t.legal[
                'We may revise these Terms from time to time at our sole discretion. We will notify you of any changes by posting the new Terms on this page with a new effective date. Your continued use of the service after any changes to the Terms constitutes acceptance of those changes.'
              ]
            }
          </p>

          <h2>{t.legal['13. Governing Law']}</h2>
          <p>
            {
              t.legal[
                'These Terms shall be governed by and construed in accordance with the laws of Delaware, without regard to its conflict of law principles. Any legal action or proceeding arising under these Terms shall be brought exclusively in the courts located in Delaware, and you consent to the jurisdiction of such courts.'
              ]
            }
          </p>

          <h2>{t.legal['14. DPA & List of Subprocessors']}</h2>
          <p>
            {t.legal['Please email']}{' '}
            <a
              href="mailto:mo@open.cx"
              className="text-primary hover:underline"
            >
              mo@open.cx
            </a>
          </p>

          <h2>{t.legal['15. Contact Us']}</h2>
          <p>
            {
              t.legal[
                'If you have any questions about these Terms and Conditions, please contact us at:'
              ]
            }
            <br />
            {t.legal['Email:']}{' '}
            <a
              href="mailto:mo@open.cx"
              className="text-primary hover:underline"
            >
              mo@open.cx
            </a>
          </p>

          <h2>{t.legal['16. SMS Messaging Program']}</h2>
          <p>
            {
              t.legal[
                'Open operates text messaging programs for users who expressly subscribe to them. You subscribe by sending the keyword START to the number published for the program, or by providing your mobile number to us and agreeing to receive text messages about the request you have opened. We send only the messages described when you subscribed. We do not send promotional or marketing text messages under these programs.'
              ]
            }
          </p>
          <p>
            {
              t.legal[
                'Message frequency varies and depends on your activity, typically fewer than five messages per request. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.'
              ]
            }
          </p>
          <p>
            {
              t.legal[
                'You can cancel at any time by replying STOP to any message, after which we send one confirmation and no further messages. Reply HELP to any message for assistance, or contact us at the address below.'
              ]
            }
          </p>
          <p>
            {
              t.legal[
                'We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes. Consent to receive text messages is collected for each program directly and is never a condition of purchase.'
              ]
            }
          </p>
        </div>
      </div>
    </section>
  );
};

export default TermsPage;
